import {
    Editor,
    Element,
    Node,
    Path,
    Range,
    Text,
    Transforms,
    type BlockNode,
    type Location,
} from "slate";

interface Rule {
    endPattern: RegExp;
    startPattern: RegExp;
    mustBeSingleLeaf?: boolean;
    apply: (
        editor: Editor,
        range: Range,
        startMatch: PatternMatch,
        endMatch: PatternMatch,
    ) => void;
}

interface PatternMatch {
    index: number;
    length: number;
    path: Path;
}

const rules: Rule[] = [
    {
        endPattern: /\*\*/,
        startPattern: /\*\*/,
        apply: applyMarkToRange("bold"),
    },
    {
        endPattern: /__/,
        startPattern: /\b__/,
        apply: applyMarkToRange("bold"),
    },
    {
        endPattern: /\*/,
        startPattern: /\*/,
        apply: applyMarkToRange("italic"),
    },
    {
        endPattern: /_/,
        startPattern: /\b_/,
        apply: applyMarkToRange("italic"),
    },
    {
        endPattern: /\\\)/,
        startPattern: /\\\(/,
        mustBeSingleLeaf: true,
        apply: wrapRangeInMathElement,
    },
    {
        endPattern: /\$\$/,
        startPattern: /\$\$/,
        mustBeSingleLeaf: true,
        apply: wrapRangeInMathElement,
    },
    {
        endPattern: /\]\([^)]+\)/,
        startPattern: /!\[/,
        apply: wrapRangeInImageElement,
    },
    {
        endPattern: /\]\([^)]+\)/,
        startPattern: /\[/,
        apply: wrapRangeInLinkElement,
    },
];

/**
 * This is a Slate plugin that adds support for autoformatting Markdown-like
 * inlines (e.g. text surrounded by "**"s turns bold).
 *
 * Block formatting (e.g. headings) should be handled by a different plugin.
 * See the `withBlockAutoformat` plugin.
 */
export const withInlineAutoformat = (editor: Editor): Editor => {
    const {insertText} = editor;

    editor.insertText = (newText, options) => {
        // If the newText contains special tokens (like "*"), we split the text
        // on these tokens and insert all the parts separately, one by one, so
        // that it's easier for the logic later to parse. For example, if the
        // string "**[wow](wow.com)**" is inserted in one, we'll insert "**",
        // "[wow](wow.com)", and then "**", so that the bold and link formats
        // both get handled.
        if (insertTextSplitBySpecialTokens(editor, newText)) {
            // If this logic ran (meaning newText contained these special
            // tokens), we already processed and inserted the text, so we bail
            // out early.
            return;
        }

        // First, insert the text normally using the default `insertText`
        // implementation. We'll handle the autoformatting afterwards.
        insertText(newText, options);

        const blockPath = getParentBlockPath(editor);
        const [leafNode, leafPath] = getCurrentLeafNode(editor);
        const leafText = leafNode.text;

        // We go through the rules listed above and try to find the first one
        // that should apply.
        outer: for (const rule of rules) {
            const endMatch = lastIndexOf(leafText, leafPath, rule.endPattern);
            if (!endMatch) {
                continue;
            }

            const startMatchWithinLeaf = lastIndexOf(
                leafText.substring(0, endMatch.index),
                leafPath,
                rule.startPattern,
            );
            if (
                // Found a match within the same leaf node...
                startMatchWithinLeaf &&
                // and there are more than 0 characters between start and end
                endMatch.index - startMatchWithinLeaf.length >
                    startMatchWithinLeaf.index
            ) {
                // Apply the rule and break; no need to keep searching.
                rule.apply(
                    editor,
                    {
                        anchor: {
                            path: leafPath,
                            offset: startMatchWithinLeaf.index,
                        },
                        focus: {
                            path: leafPath,
                            offset: endMatch.index + endMatch.length,
                        },
                    },
                    startMatchWithinLeaf,
                    endMatch,
                );
                break outer;
            }

            if (rule.mustBeSingleLeaf) {
                // Some rules (like math) should not span multiple leaf nodes,
                // so if we haven't found a match here, just continue.
                continue;
            }

            // Iterate through all the text nodes within the current block,
            // that come before the current leaf, in reverse order. We do this
            // to search backwards for the start pattern - we'll break and
            // apply the rule for the first match we find.
            for (const [node, path] of Editor.nodes(editor, {
                at: blockPath,
                reverse: true,
                match: (n, p) => Text.isText(n) && Path.isBefore(p, leafPath),
            })) {
                const text = Node.string(node);
                const startMatch = lastIndexOf(text, path, rule.startPattern);
                if (!startMatch) {
                    continue;
                }

                rule.apply(
                    editor,
                    {
                        anchor: {
                            path,
                            offset: startMatch.index,
                        },
                        focus: {
                            path: leafPath,
                            offset: endMatch.index + endMatch.length,
                        },
                    },
                    startMatch,
                    endMatch,
                );
                break outer;
            }
        }
    };

    return editor;
};

function applyMarkToRange(mark: string) {
    return (
        editor: Editor,
        range: Range,
        startMatch: PatternMatch,
        endMatch: PatternMatch,
    ) => {
        Editor.withoutNormalizing(editor, () => {
            // Use pointRefs to save the start and end points of the range: they
            // may shift around after the setNodes call that follows, but this way
            // Slate will keep them updated properly:
            const startRef = Editor.pointRef(editor, Range.start(range));
            const endRef = Editor.pointRef(editor, Range.end(range));

            // Set the mark on the text nodes within the given range.
            // Here, "**hello**" will become "<b>**hello**</b>"
            Transforms.setNodes(
                editor,
                {[mark]: true},
                {
                    at: range,
                    split: true,
                    voids: true,
                    match: (n) => Text.isText(n),
                },
            );

            // From the end of the range, delete backwards N characters, where N is
            // the length of the end pattern (e.g. the last "**")
            // Here, "<b>**hello**</b>" will become "<b>**hello</b>"
            Transforms.delete(editor, {
                at: endRef.unref()!,
                distance: endMatch.length,
                unit: "character",
                reverse: true,
            });

            // From the start of the range, delete forwards N characters, where N is
            // the length of the start pattern (e.g. the first "**")
            // Here, "<b>**hello</b>" will become "<b>hello</b>"
            Transforms.delete(editor, {
                at: startRef.unref()!,
                distance: startMatch.length,
                unit: "character",
            });

            // Now that we've handled the start/end characters, remove the mark to
            // keep it from applying to future text that is typed after this.
            Editor.removeMark(editor, mark);
        });
    };
}

function wrapRangeInMathElement(
    editor: Editor,
    range: Range,
    startMatch: PatternMatch,
    endMatch: PatternMatch,
) {
    // We know that math elements only span a single leaf, so grab the text
    // of the leaf and get the substring matched by our rule. This will be the
    // TeX content that we use for the element.
    // ex: leafText: "I love $$47$$ math!" -> mathText: "47"
    const [leafNode, leafPath] = Editor.leaf(editor, range.anchor);
    const leafText = Node.string(leafNode);
    const mathText = leafText.substring(
        startMatch.index + startMatch.length,
        endMatch.index,
    );

    // We also extract the leaf text that comes _before_ and _after_ the math,
    // if there is any. We'll need to insert this later if non-empty.
    // ex: textBeforeMath: "I love ", textAfterMath: " math!"
    const textBeforeMath = leafText.substring(0, startMatch.index);
    const textAfterMath = leafText.substring(endMatch.index + endMatch.length);

    // Delete the text from the leaf, leaving an empty leaf. We'll add it back!
    Transforms.delete(editor, {
        at: {
            anchor: Editor.start(editor, leafPath),
            focus: Editor.end(editor, leafPath),
        },
    });

    if (textBeforeMath) {
        // Add back the text that came before the math, if any:
        Transforms.insertNodes(editor, {...leafNode, text: textBeforeMath});
        // content is now: "I love "
    }

    // Insert a math node with the TeX string we extracted earlier:
    Transforms.insertNodes(editor, {
        type: "math-inline",
        tex: mathText,
        children: [{text: ""}],
    });
    // content is now: "I love <tex>47</tex>"

    // Normalize to ensure there is a text node after the math void, and
    // move the cursor out of the void into that text node, as suggested
    // here: https://github.com/ianstormtaylor/slate/issues/234
    Editor.normalize(editor, {force: true});
    Transforms.move(editor, {distance: 1, unit: "character"});

    if (textAfterMath) {
        // Add back the text that came after the math, if any:
        Transforms.insertNodes(editor, {...leafNode, text: textAfterMath});
        // content is now: "I love <tex>47</tex> math!"
    }
}

function wrapRangeInLinkElement(
    editor: Editor,
    range: Range,
    startMatch: PatternMatch,
    endMatch: PatternMatch,
) {
    // Use pointRefs to save the start and end points of the range: they
    // may shift around after the setNodes call that follows, but this way
    // Slate will keep them updated properly:
    const startRef = Editor.pointRef(editor, Range.start(range));
    const endRef = Editor.pointRef(editor, Range.end(range));

    // Extract the URL based on the end match. Note that the pattern was
    // matching "](some-text)", so we need to adjust for the leading "](" and
    // trailing ")" characters.
    const [endLeafNode] = Editor.leaf(editor, range.focus);
    const endLeafText = Node.string(endLeafNode);
    const url = endLeafText.substring(
        endMatch.index + 2,
        endMatch.index + endMatch.length - 1,
    );

    // We wrap the whole range in a link with the extracted URL. At this point,
    // we'll get a link element that looks like `[hello](example.com)` - it'll
    // still have the leading and trailing Markdown-isms.
    Transforms.wrapNodes(
        editor,
        {type: "link", url, children: []},
        {at: range, split: true},
    );

    // From the end of the range, delete backwards N characters. For example,
    // if we matched `[hello](example.com)`, the end match would be the string
    // `](example.com)` - so this delete would trim the end and leave us with
    // a link that looked like `[hello`.
    Transforms.delete(editor, {
        at: endRef.unref()!,
        distance: endMatch.length,
        unit: "character",
        reverse: true,
    });

    // From the start of the range, delete the start match characters. If we
    // have a link now that looks like `[hello`, this would trim off the first
    // `[` and leave us with just `hello`, which is what we want!
    Transforms.delete(editor, {
        at: startRef.unref()!,
        distance: startMatch.length,
        unit: "character",
    });

    // After the children, move the selection to the point just after the
    // link node, so that following inlines won't be made part of the link
    const pointAfterLink = Editor.after(editor, editor.selection!);
    if (pointAfterLink) {
        Transforms.select(editor, pointAfterLink);
    }
}

function wrapRangeInImageElement(
    editor: Editor,
    range: Range,
    startMatch: PatternMatch,
    endMatch: PatternMatch,
) {
    const endRef = Editor.pointRef(editor, Range.end(range), {
        affinity: "forward",
    });

    // Extract the URL based on the end match. Note that the pattern was
    // matching "](some-text)", so we need to adjust for the leading "](" and
    // trailing ")" characters.
    const [endLeafNode] = Editor.leaf(editor, range.focus);
    const endLeafText = Node.string(endLeafNode);
    const url = endLeafText.substring(
        endMatch.index + 2,
        endMatch.index + endMatch.length - 1,
    );

    const [startLeafNode] = Editor.leaf(editor, range.anchor);
    const startLeafText = Node.string(startLeafNode);
    const alt = startLeafText.substring(startMatch.index + 2, endMatch.index);

    // Delete the whole range, including the start and end markers:
    Transforms.delete(editor, {at: range});

    // Insert an image with the extracted URL and alt text.
    Transforms.insertNodes(
        editor,
        {type: "image", url, alt, children: []},
        {at: endRef.unref()!},
    );

    // We want the cursor to end up right after the image node, so that typing
    // can continue after it. This next bit of trickiness implements that! Move
    // ahead one character, find the image node, then move the cursor to the
    // point just after the image node.
    Transforms.move(editor, {distance: 1, unit: "character"});
    const [image] = Editor.nodes(editor, {
        match: (n) => Element.isElement(n) && n.type === "image",
    });
    if (image) {
        const pointAfterImage = Editor.after(editor, image[1]);
        Transforms.select(editor, pointAfterImage!);
    }
}

const insertTextSplitBySpecialTokens = (
    editor: Editor,
    text: string,
): boolean => {
    let lastIndex = 0;
    // Split on some special tokens:
    // - sequences of "*" or "_" (for bold/italic text)
    // - "\(" (for inline math)
    for (const match of text.matchAll(/([*_]+|\\\()/g)) {
        if (match[0].length === text.length) {
            // If we found a match that was the full length of the text, we
            // don't want to do any splitting and we bail out early. This case
            // handles strings that are, for example, entirely "*" or "**".
            return false;
        }

        if (match[0].length === 0) {
            continue;
        }

        editor.insertText(text.substring(lastIndex, match.index!));
        editor.insertText(match[0]);
        lastIndex = match.index! + match[0].length;
    }

    if (lastIndex > 0) {
        const lastPart = text.substring(lastIndex);
        if (lastPart) {
            editor.insertText(lastPart);
        }
        return true;
    }

    return false;
};

const lastIndexOf = (
    text: string,
    path: Path,
    pattern: RegExp,
): PatternMatch | null => {
    const globalPattern = new RegExp(pattern, "g");
    const matches = text.match(globalPattern);
    if (matches) {
        const lastMatch = matches[matches.length - 1];
        const index = text.lastIndexOf(lastMatch);
        return {
            index,
            length: lastMatch.length,
            path,
        };
    }

    return null;
};

const getCurrentLeafNode = (editor: Editor) => {
    const selection = editor.selection || {
        anchor: Editor.end(editor, []),
        focus: Editor.end(editor, []),
    };
    return Editor.leaf(editor, selection);
};

const getParentBlockPath = (editor: Editor, at?: Location) => {
    const [, path] = Editor.above<BlockNode>(editor, {
        at,
        match: (n) => Element.isElement(n) && Editor.isBlock(editor, n),
    })!;

    return path;
};
